<template>
  <el-input
    ref="inputRef"
    :model-value="displayValue"
    class="catalog-person-form__mobile-phone-input"
    :placeholder="AZ_MOBILE_PHONE_LOCAL_MASK"
    inputmode="tel"
    @update:model-value="onPasteInput"
    @keydown="onKeydown"
    @focus="onFocus"
    @click="onPointer"
    @mouseup="onPointer"
    @blur="onBlur"
  >
    <template #prepend>+994</template>
  </el-input>
</template>

<script>
import {
  AZ_MOBILE_PHONE_LOCAL_MASK,
  azMobileMaskCursorIndex,
  buildAzMobilePhoneFromLocal,
  extractAzMobileLocalPart,
  formatAzMobileLocalMask,
  parseAzMobileLocalMask,
} from "./catalogPhone.js";

const ALLOWED_KEYS = new Set([
  "Tab",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

export default {
  name: "CatalogMobilePhoneInput",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      AZ_MOBILE_PHONE_LOCAL_MASK,
      focused: false,
    };
  },
  computed: {
    localDigits() {
      return extractAzMobileLocalPart(this.modelValue);
    },
    displayValue() {
      if (!this.localDigits && !this.focused) return "";
      return formatAzMobileLocalMask(this.localDigits);
    },
  },
  methods: {
    getNativeInput() {
      return this.$refs.inputRef?.input ?? null;
    },
    moveCursorToDigitEnd() {
      const input = this.getNativeInput();
      if (!input) return;
      const pos = azMobileMaskCursorIndex(this.localDigits.length);
      input.setSelectionRange(pos, pos);
    },
    setDigits(digits) {
      this.$emit("update:modelValue", buildAzMobilePhoneFromLocal(digits));
      this.$nextTick(() => this.moveCursorToDigitEnd());
    },
    onFocus() {
      this.focused = true;
      this.$nextTick(() => this.moveCursorToDigitEnd());
    },
    onBlur() {
      this.focused = false;
    },
    onPointer() {
      this.$nextTick(() => this.moveCursorToDigitEnd());
    },
    hasTextSelection() {
      const input = this.getNativeInput();
      return Boolean(input && input.selectionStart !== input.selectionEnd);
    },
    onKeydown(event) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (ALLOWED_KEYS.has(event.key)) {
        if (
          event.key === "ArrowLeft" ||
          event.key === "ArrowRight" ||
          event.key === "Home" ||
          event.key === "End"
        ) {
          event.preventDefault();
          this.$nextTick(() => this.moveCursorToDigitEnd());
        }
        return;
      }

      const digits = this.localDigits;
      const selected = this.hasTextSelection();

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        if (selected) {
          this.setDigits("");
          return;
        }
        if (digits) {
          this.setDigits(digits.slice(0, -1));
        }
        return;
      }

      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        if (selected) {
          this.setDigits(event.key);
          return;
        }
        if (digits.length >= 9) return;
        this.setDigits(`${digits}${event.key}`);
        return;
      }

      if (event.key.length === 1) {
        event.preventDefault();
      }
    },
    onPasteInput(value) {
      const next = parseAzMobileLocalMask(value);
      if (next === this.localDigits) {
        this.$nextTick(() => this.moveCursorToDigitEnd());
        return;
      }
      this.setDigits(next);
    },
  },
};
</script>
